import React, { createContext, useContext, useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../../supabase/supabase.config';

interface AppReadyContextType {
  isAppReady: boolean;
}

interface AppReadyContextProps {
  children: React.ReactNode;
}

export const AppReadyContext = createContext<AppReadyContextType | undefined>(undefined);

export const AppReadyProvider: React.FC<AppReadyContextProps> = ({ children }) => {
  const [isAppReady, setIsAppReady] = React.useState(false);

  useEffect(() => {
    const initializeAutoRefresh = async () => {
      try {
        // Start auto-refresh when the app initializes
        console.log('Initializing Supabase auto-refresh');
        supabase.auth.startAutoRefresh();
        setIsAppReady(true);

        // Only set up app state listeners on native platforms
        if (Capacitor.isNativePlatform()) {
          console.log('Setting up app state change listeners for native platform');
          
          // Listen for app state changes
          const appStateListener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
            console.log('App state changed:', isActive ? 'active' : 'inactive');
            
            if (isActive) {
              console.log('App became active - starting auto-refresh');
              supabase.auth.startAutoRefresh();
            } else {
              console.log('App became inactive - stopping auto-refresh');
              supabase.auth.stopAutoRefresh();
            }
          });

          // Cleanup listener on component unmount
          return () => {
            console.log('Cleaning up app state listener');
            appStateListener.remove();
            supabase.auth.stopAutoRefresh();
          };
        } else {
          console.log('Web platform detected - using visibility API for auto-refresh');
          
          // For web platform, use document visibility API
          const handleVisibilityChange = () => {
            if (document.hidden) {
              console.log('Page became hidden - stopping auto-refresh');
              supabase.auth.stopAutoRefresh();
            } else {
              console.log('Page became visible - starting auto-refresh');
              supabase.auth.startAutoRefresh();
            }
          };

          document.addEventListener('visibilitychange', handleVisibilityChange);

          // Cleanup listener on component unmount
          return () => {
            console.log('Cleaning up visibility change listener');
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            supabase.auth.stopAutoRefresh();
          };
        }
      } catch (error) {
        console.error('Error initializing auto-refresh:', error);
        setIsAppReady(true); // Still mark as ready even if auto-refresh fails
      }
    };

    initializeAutoRefresh();
  }, []);

  return (
    <AppReadyContext.Provider value={{ isAppReady }}>
      {children}
    </AppReadyContext.Provider>
  );
};

export const useAppReady = () => {
  const context = useContext(AppReadyContext);
  if (context === undefined) {
    throw new Error('useAppReady must be used within an AppReadyProvider');
  }
  return context;
}; 